import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, CheckCircle2, Calendar as CalendarIcon } from "lucide-react";
import Litepicker from "@/components/Base/Litepicker";
import SalesProductEntryList, { SALES_LAST_GROUP_KEY } from "./SalesProductEntryList";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
const SALES_PENDING_CONFIRM_KEY = "salesPendingConfirmPayload";
const SALES_IMAGE_CACHE_NAME = "sales-product-images-v1";

function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function malaysiaDateLabelFromDate(d: Date) {
  // Example: 04-Mar-2026
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const label = fmt.format(d); // e.g. "04 Mar 2026"
  return label.replace(/\s+/g, "-");
}

function malaysiaWeekdayAbbrFromDate(d: Date) {
  // Example: Tue
  const fmt = new Intl.DateTimeFormat("en-US", { timeZone: MALAYSIA_TZ, weekday: "short" });
  return fmt.format(d);
}

function malaysiaDateLabelWithWeekdayFromDate(d: Date) {
  // Example: 04-Mar-2026 (Tue)
  return `${malaysiaDateLabelFromDate(d)} (${malaysiaWeekdayAbbrFromDate(d)})`;
}

function dateKeyToUTCDate(key: string) {
  const [y, m, dd] = key.split("-").map((x) => Number(x));
  return new Date(Date.UTC(y, m - 1, dd));
}

/** Inclusive lookback from today: today + 6 previous days = 7-day window. */
const SUPERVISOR_SALES_LOOKBACK_DAYS = 6;

function daysBetweenDateKeys(newerKey: string, olderKey: string) {
  const newer = dateKeyToUTCDate(newerKey);
  const older = dateKeyToUTCDate(olderKey);
  return Math.round((newer.getTime() - older.getTime()) / (24 * 60 * 60 * 1000));
}

export default function Sales() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [productGroupNames, setProductGroupNames] = useState<string[]>([]);
  const [activeGroupName, setActiveGroupName] = useState<string>("");

  // Date-only selection (GMT+8). Submit is allowed for any unsubmitted day in the last 7 days.
  const todayUTC = useMemo(() => {
    const key = malaysiaDateKeyFromDate(new Date());
    const [y, m, dd] = key.split("-").map((x) => Number(x));
    return new Date(Date.UTC(y, m - 1, dd));
  }, []);
  const [daysBack, setDaysBack] = useState(0);
  const selectedUTCDate = useMemo(() => {
    const d = new Date(todayUTC);
    d.setUTCDate(d.getUTCDate() - daysBack);
    return d;
  }, [todayUTC, daysBack]);
  const selectedDateKey = useMemo(() => malaysiaDateKeyFromDate(selectedUTCDate), [selectedUTCDate]);
  const isToday = daysBack === 0;
  const isYesterday = daysBack === 1;
  const isWithinEditableWindow = daysBack >= 0 && daysBack <= SUPERVISOR_SALES_LOOKBACK_DAYS;
  const todayDateKeyForPicker = useMemo(() => malaysiaDateKeyFromDate(todayUTC), [todayUTC]);
  const oldestDateKeyForPicker = useMemo(() => {
    const d = new Date(todayUTC);
    d.setUTCDate(d.getUTCDate() - SUPERVISOR_SALES_LOOKBACK_DAYS);
    return malaysiaDateKeyFromDate(d);
  }, [todayUTC]);
  const windowDateKeys = useMemo(
    () =>
      Array.from({ length: SUPERVISOR_SALES_LOOKBACK_DAYS + 1 }, (_, i) => {
        const d = new Date(todayUTC);
        d.setUTCDate(d.getUTCDate() - i);
        return malaysiaDateKeyFromDate(d);
      }),
    [todayUTC]
  );
  // The supervisor must explicitly pick which date they're entering sales for before the quantity
  // inputs unlock — never assume "Today" just because that's what the arrows happen to show.
  const [confirmedEntryDateKey, setConfirmedEntryDateKey] = useState<string | null>(null);
  const canEditSelectedDate = isWithinEditableWindow && confirmedEntryDateKey === selectedDateKey;

  type SalesProductRow = {
    productId: string;
    name: string;
    sku: string | null;
    qtySold: number;
    tradeInQty?: number;
    sellingPrice: number | null;
    reimbursement?: number | null;
    rebate?: number | null;
    productImageUrl?: string | null;
    productImageVersion?: string | null;
    value: number | null;
    allowTradeIn?: boolean;
    tradeInValue?: number | null;
  };

  type SalesGroupRow = { groupName: string; products: SalesProductRow[] };
  type TeamSalesResponse = {
    team: { id: string; name: string } | null;
    groups: SalesGroupRow[];
    salesSubmission?: {
      isSubmitted: boolean;
      bankedAt: string | null;
      expectedTotal: number;
      remainingAmount: number;
    };
  };

  type StockProductRow = { productId: string; openingQty: number; closingQty: number };
  type StockGroupRow = { groupName: string; products: StockProductRow[] };
  type TeamStockResponse = { team: { id: string; name: string } | null; groups: StockGroupRow[] };

  const [salesData, setSalesData] = useState<TeamSalesResponse | null>(null);
  const [availableQtyByProductId, setAvailableQtyByProductId] = useState<Record<string, number>>({});
  const [showDateRequiredPrompt, setShowDateRequiredPrompt] = useState(false);
  const [submittedDatePromptKey, setSubmittedDatePromptKey] = useState<string | null>(null);
  const [litepickerKey, setLitepickerKey] = useState(0);
  const [submittedByDateKey, setSubmittedByDateKey] = useState<Record<string, boolean>>({});
  const [remainingByDateKey, setRemainingByDateKey] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Editable qty sold only for today.
  const [qtySoldByProductId, setQtySoldByProductId] = useState<Record<string, number>>({});
  const [initialQtySoldByProductId, setInitialQtySoldByProductId] = useState<Record<string, number>>({});
  // Trade-in qty keyed by product (shown when TradeIn.allowTradeIn is true).
  const [tradeInQtyByProductId, setTradeInQtyByProductId] = useState<Record<string, number>>({});
  const productImageBlobUrlsRef = useRef<Map<string, string>>(new Map());
  const [cachedImageSrcByProductId, setCachedImageSrcByProductId] = useState<Record<string, string>>({});
  const [brokenImageProductIds, setBrokenImageProductIds] = useState<Record<string, boolean>>({});
  const isSubmittedForSelectedDate = Boolean(
    submittedByDateKey[selectedDateKey] ?? salesData?.salesSubmission?.isSubmitted
  );
  const isSubmittedLocked =
    Boolean(confirmedEntryDateKey) && confirmedEntryDateKey === selectedDateKey && isSubmittedForSelectedDate;
  const selectedRemainingAmount =
    confirmedEntryDateKey === selectedDateKey
      ? (remainingByDateKey[selectedDateKey] ?? salesData?.salesSubmission?.remainingAmount ?? 0)
      : 0;
  const isFullySettled = selectedRemainingAmount <= 0.004;

  // Date picker stays visible after submission so the supervisor can switch to another open day.
  const showEntryDateSection = true;
  // The Litepicker library auto-fires onChange with "today" once on mount with an empty value.
  const pickerAutoFillGuardRef = useRef(true);
  // After the user dismisses the submitted-date prompt, ignore that same date until they pick another.
  const dismissedSubmittedDateRef = useRef<string | null>(null);

  useEffect(() => {
    const loadGroups = async () => {
      if (!token) {
        setError("Your login has expired. Please log out and log in again.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/product-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json: { groups: string[] } = await res.json().catch(() => ({ groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load product groups (${res.status}).`);
          return;
        }

        setProductGroupNames(json.groups ?? []);
        const groups = json.groups ?? [];
        const remembered = localStorage.getItem(SALES_LAST_GROUP_KEY) || "";
        const nextActive = groups.includes(remembered) ? remembered : groups[0] ?? "";
        setActiveGroupName(nextActive);
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadSales = async () => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load sales (${res.status}).`);
          return;
        }

        setSalesData(json);
        setBrokenImageProductIds({});

        if (json.salesSubmission) {
          setSubmittedByDateKey((prev) => ({
            ...prev,
            [selectedDateKey]: Boolean(json.salesSubmission?.isSubmitted),
          }));
          const remaining = Number(json.salesSubmission?.remainingAmount ?? 0);
          setRemainingByDateKey((prev) => ({
            ...prev,
            [selectedDateKey]: Number.isFinite(remaining) ? Math.max(0, remaining) : 0,
          }));
        }

        // initialize edit state for dates inside the last-7-day window
        if (isWithinEditableWindow) {
          const nextQty: Record<string, number> = {};
          const nextInitial: Record<string, number> = {};
          const nextTradeInQty: Record<string, number> = {};
          for (const g of json.groups ?? []) {
            for (const p of g.products ?? []) {
              nextQty[p.productId] = p.qtySold ?? 0;
              nextInitial[p.productId] = p.qtySold ?? 0;
              nextTradeInQty[p.productId] = p.tradeInQty ?? 0;
            }
          }
          setQtySoldByProductId(nextQty);
          setInitialQtySoldByProductId(nextInitial);
          setTradeInQtyByProductId(nextTradeInQty);
        } else {
          setQtySoldByProductId({});
          setInitialQtySoldByProductId({});
          setTradeInQtyByProductId({});
        }
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey]);

  // Available stock (opening qty for the selected date) so Pack Sold can never exceed what the team currently has.
  useEffect(() => {
    const loadAvailableStock = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-stock?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamStockResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) return;

        const nextAvailable: Record<string, number> = {};
        for (const g of json.groups ?? []) {
          for (const p of g.products ?? []) {
            nextAvailable[p.productId] = p.openingQty ?? 0;
          }
        }
        setAvailableQtyByProductId(nextAvailable);
      } catch {
        // Silently ignore; Pack Sold will simply not be capped if stock data is unavailable.
      }
    };

    void loadAvailableStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey]);

  // Preload submission status for the last 7 days so the date picker can block already-submitted days.
  useEffect(() => {
    if (!token) return;
    const preload = async (dateKey: string) => {
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${dateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) return;
        setSubmittedByDateKey((prev) => ({
          ...prev,
          [dateKey]: Boolean(json.salesSubmission?.isSubmitted),
        }));
        const remaining = Number(json.salesSubmission?.remainingAmount ?? 0);
        setRemainingByDateKey((prev) => ({
          ...prev,
          [dateKey]: Number.isFinite(remaining) ? Math.max(0, remaining) : 0,
        }));
      } catch {
        // Ignore — handleSelectEntryDate will re-fetch on demand.
      }
    };
    windowDateKeys.forEach((dateKey) => void preload(dateKey));
  }, [apiUrl, token, windowDateKeys]);

  useEffect(() => {
    return () => {
      productImageBlobUrlsRef.current.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
      productImageBlobUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const allProducts = (salesData?.groups ?? []).flatMap((g) => g.products ?? []);
    const activeIds = new Set(allProducts.map((p) => p.productId));

    // Remove blob URLs that no longer exist in the current payload.
    const removedIds: string[] = [];
    productImageBlobUrlsRef.current.forEach((blobUrl, productId) => {
      if (!activeIds.has(productId)) {
        URL.revokeObjectURL(blobUrl);
        productImageBlobUrlsRef.current.delete(productId);
        removedIds.push(productId);
      }
    });
    if (removedIds.length > 0) {
      setCachedImageSrcByProductId((prev) => {
        const next = { ...prev };
        removedIds.forEach((id) => delete next[id]);
        return next;
      });
    }

    if (!("caches" in window) || allProducts.length === 0) return;

    let cancelled = false;
    const loadAndCacheImages = async () => {
      const cache = await caches.open(SALES_IMAGE_CACHE_NAME);

      for (const product of allProducts) {
        if (cancelled) return;

        const imageUrl = product.productImageUrl ?? "";
        const imageVersion = product.productImageVersion ?? imageUrl;
        if (!imageUrl || !imageVersion) continue;

        const cacheKey = `${window.location.origin}/__sales_product_image_cache__?productId=${encodeURIComponent(
          product.productId
        )}&v=${encodeURIComponent(imageVersion)}`;

        try {
          let response = await cache.match(cacheKey);
          if (!response) {
            const fetched = await fetch(imageUrl, { cache: "force-cache" });
            if (!fetched.ok) continue;
            const freshBlob = await fetched.blob();
            response = new Response(freshBlob, {
              headers: { "Content-Type": freshBlob.type || "image/jpeg" },
            });
            await cache.put(cacheKey, response.clone());
          }

          const blob = await response.blob();
          if (cancelled) return;

          const oldBlobUrl = productImageBlobUrlsRef.current.get(product.productId);
          if (oldBlobUrl) URL.revokeObjectURL(oldBlobUrl);

          const blobUrl = URL.createObjectURL(blob);
          productImageBlobUrlsRef.current.set(product.productId, blobUrl);
          setCachedImageSrcByProductId((prev) => ({ ...prev, [product.productId]: blobUrl }));
        } catch {
          // Keep UI fallback icon when image cannot be fetched.
        }
      }
    };

    void loadAndCacheImages();

    return () => {
      cancelled = true;
    };
  }, [salesData]);

  const handleSelectEntryDate = async (value: string) => {
    if (!value) return;
    const selectedDaysBack = daysBetweenDateKeys(todayDateKeyForPicker, value);
    if (selectedDaysBack < 0 || selectedDaysBack > SUPERVISOR_SALES_LOOKBACK_DAYS) {
      setSubmitError("You can only enter sales for the last 7 days.");
      return;
    }
    setSubmitError("");

    let isSubmitted = submittedByDateKey[value];
    if (isSubmitted === undefined) {
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-sales?date=${value}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamSalesResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        isSubmitted = Boolean(json.salesSubmission?.isSubmitted);
        setSubmittedByDateKey((prev) => ({ ...prev, [value]: isSubmitted }));
        const remaining = Number(json.salesSubmission?.remainingAmount ?? 0);
        setRemainingByDateKey((prev) => ({
          ...prev,
          [value]: Number.isFinite(remaining) ? Math.max(0, remaining) : 0,
        }));
      } catch {
        setSubmitError("Unable to verify sales status. Please try again.");
        setLitepickerKey((k) => k + 1);
        pickerAutoFillGuardRef.current = true;
        return;
      }
    }

    if (isSubmitted) {
      // Picker reset can re-fire onChange with the date we're already on — don't loop the modal.
      if (value === confirmedEntryDateKey || value === dismissedSubmittedDateRef.current) {
        setLitepickerKey((k) => k + 1);
        pickerAutoFillGuardRef.current = true;
        return;
      }
      dismissedSubmittedDateRef.current = null;
      setSubmittedDatePromptKey(value);
      setLitepickerKey((k) => k + 1);
      pickerAutoFillGuardRef.current = true;
      return;
    }

    dismissedSubmittedDateRef.current = null;
    setConfirmedEntryDateKey(value);
    setDaysBack(selectedDaysBack);
  };

  const dismissSubmittedDatePrompt = () => {
    setSubmittedDatePromptKey((current) => {
      if (current) dismissedSubmittedDateRef.current = current;
      return null;
    });
    setLitepickerKey((k) => k + 1);
    pickerAutoFillGuardRef.current = true;
  };

  const handleSubmit = async () => {
    if (!canEditSelectedDate || isSubmittedLocked) return;
    setSubmitError("");

    // Defensive re-check against latest known stock (e.g. another device may have sold/received stock since load).
    const productNameById = new Map<string, string>();
    for (const g of salesData?.groups ?? []) {
      for (const p of g.products ?? []) productNameById.set(p.productId, p.name);
    }
    // Combined Qty Sold + Trade-In Qty must not exceed available stock per product.
    const productIdsToCheck = new Set([
      ...Object.keys(qtySoldByProductId),
      ...Object.keys(tradeInQtyByProductId),
    ]);
    for (const productId of productIdsToCheck) {
      const available = availableQtyByProductId[productId];
      if (typeof available !== "number") continue;
      const qtySold = qtySoldByProductId[productId] ?? 0;
      const tradeInQty = tradeInQtyByProductId[productId] ?? 0;
      const combined = qtySold + tradeInQty;
      if (combined > available) {
        const name = productNameById.get(productId) ?? "A product";
        setSubmitError(
          `${name}: Qty Sold (${qtySold}) + Trade-In Qty (${tradeInQty}) = ${combined} exceeds available stock (${available}). Please adjust before proceeding.`
        );
        return;
      }
    }

    setSubmitting(true);

    try {
      const productIds = new Set([
        ...Object.keys(qtySoldByProductId),
        ...Object.keys(tradeInQtyByProductId),
      ]);
      const lines: Array<{ productId: string; qtySold: number; tradeInQty: number }> = Array.from(productIds)
        .map((productId) => ({
          productId,
          qtySold: Math.max(0, Math.floor(qtySoldByProductId[productId] ?? 0)),
          tradeInQty: Math.max(0, Math.floor(tradeInQtyByProductId[productId] ?? 0)),
        }))
        .filter((line) => line.qtySold > 0 || line.tradeInQty > 0);
      const totalQty = lines.reduce((sum, line) => sum + (line.qtySold ?? 0), 0);

      const stagedGroups =
        (salesData?.groups ?? []).map((g) => ({
          groupName: g.groupName,
          products: (g.products ?? []).map((p) => {
            const qty = qtySoldByProductId[p.productId] ?? p.qtySold ?? 0;
            const tradeInQty = tradeInQtyByProductId[p.productId] ?? 0;
            const price = p.sellingPrice ?? null;
            const reimbursement = typeof p.reimbursement === "number" ? p.reimbursement : 0;
            const rebate = typeof p.rebate === "number" ? p.rebate : 0;
            const tradeInUnit = typeof p.tradeInValue === "number" ? p.tradeInValue : null;
            return {
              productId: p.productId,
              name: p.name,
              sku: p.sku,
              qtySold: qty,
              tradeInQty,
              sellingPrice: price,
              reimbursement,
              rebate,
              value: typeof price === "number" ? qty * price : null,
              tradeInValue: tradeInUnit,
              tradeInLineValue: 0,
            };
          }),
        })) ?? [];

      try {
        await fetch(`${apiUrl}/supervisor/activity/sales-input`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            salesDate: selectedDateKey,
            totalQty,
            lineCount: lines.length,
          }),
        });
      } catch {
        // Keep navigation flow even if activity logging fails.
      }

      sessionStorage.setItem(
        SALES_PENDING_CONFIRM_KEY,
        JSON.stringify({
          salesDate: selectedDateKey,
          lines,
          groups: stagedGroups,
        })
      );
      navigate("/app/sales/confirm");
    } catch {
      setSubmitError("Unable to proceed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!activeGroupName) return;
    localStorage.setItem(SALES_LAST_GROUP_KEY, activeGroupName);
  }, [activeGroupName]);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <PageLoadingOverlay show={loading} label="Loading sales…" />
      <style>{`
        /* Prevent odd mobile input styling; spinners aren't applicable for type="text". */
        input.no-number-spinners {
          -webkit-appearance: none;
        }
      `}</style>

      {/* Gradient Header */}
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>

            <div className="text-[20px] font-semibold">Sales</div>

            <button
              type="button"
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Sort (disabled for now)"
            >
              <ArrowUpDown size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Consistent breathing room between the gradient header and whatever renders first below it
          (the entry-date container or the tab buttons — depending on state). Changing the entry
          date is done by tapping the date container below, not a separate button. */}
      <div className="pt-3" />

      {/* Entry date selection — always required before quantity inputs unlock. */}
      {showEntryDateSection ? (
        <div className="px-4 pb-1">
          <div
            className={`rounded-lg border px-3 py-2 ${
              canEditSelectedDate ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
            }`}
          >
            <div className={`text-xs font-medium mb-1.5 ${canEditSelectedDate ? "text-emerald-800" : "text-amber-800"}`}>
              {canEditSelectedDate ? (
                <>
                  Entering sales for{" "}
                  <span className="font-semibold">
                    {malaysiaDateLabelWithWeekdayFromDate(selectedUTCDate)}
                    {isToday ? " (Today)" : isYesterday ? " (Yesterday)" : ""}
                  </span>{" "}
                  · tap to change
                </>
              ) : (
                "Select the date of sales"
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <div className="absolute inset-y-0 left-0 flex items-center justify-center w-9 text-slate-400 pointer-events-none">
                  <CalendarIcon size={16} />
                </div>
                <Litepicker
                  key={litepickerKey}
                  value={confirmedEntryDateKey ?? ""}
                  onChange={(e) => {
                    if (submittedDatePromptKey) return;
                    if (pickerAutoFillGuardRef.current) {
                      pickerAutoFillGuardRef.current = false;
                      return;
                    }
                    handleSelectEntryDate(e.target.value);
                  }}
                  options={{
                    format: "YYYY-MM-DD",
                    singleMode: true,
                    autoApply: true,
                    minDate: oldestDateKeyForPicker,
                    maxDate: todayDateKeyForPicker,
                  }}
                  readOnly
                  placeholder="Tap to select date"
                  // The underlying value must stay a plain ISO string (used for min/max + comparisons),
                  // so the human-friendly "dd-mmm-yyyy (Weekday)" text is rendered as an overlay below
                  // instead of changing the picker's own format.
                  className={`pl-9 text-transparent caret-transparent cursor-pointer ${
                    canEditSelectedDate ? "border-emerald-300" : "border-amber-300"
                  }`}
                />
                {confirmedEntryDateKey ? (
                  <div className="absolute inset-y-0 left-9 right-2 flex items-center pointer-events-none text-sm text-slate-900 truncate">
                    {malaysiaDateLabelWithWeekdayFromDate(dateKeyToUTCDate(confirmedEntryDateKey))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="px-4 pt-2">
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm mb-2">{error}</div>
        </div>
      ) : null}

      {loading && !salesData ? (
        <div className="bg-gray-50 px-4 pt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-white border border-gray-200 animate-pulse mb-2" />
          ))}
        </div>
      ) : null}

      {!loading ? (
        <div className="bg-gray-50 px-4 pt-2 pb-40">
          {isSubmittedLocked ? (
            <div
              className={`mb-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-between gap-2 ${
                canEditSelectedDate && !isFullySettled
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-green-200 bg-green-50 text-green-800"
              }`}
            >
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <CheckCircle2
                  size={16}
                  className={canEditSelectedDate && !isFullySettled ? "text-amber-700 shrink-0" : "text-green-700 shrink-0"}
                />
                <span className="truncate">
                  Sales submitted
                  {canEditSelectedDate && !isFullySettled ? (
                    <span className="ml-1.5 font-semibold">
                      · Difference: RM{" "}
                      {selectedRemainingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  ) : canEditSelectedDate ? (
                    <span className="ml-1.5 font-semibold">· Fully settled</span>
                  ) : null}
                </span>
              </span>
              {canEditSelectedDate ? (
                <button
                  type="button"
                  className={`shrink-0 rounded-md border bg-white px-2.5 py-1 text-xs font-semibold ${
                    isFullySettled ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"
                  }`}
                  onClick={() => {
                    sessionStorage.removeItem(SALES_PENDING_CONFIRM_KEY);
                    navigate(`/app/sales/confirm?date=${selectedDateKey}`);
                  }}
                >
                  View
                </button>
              ) : null}
            </div>
          ) : null}

          <SalesProductEntryList
            groupNames={productGroupNames}
            groups={salesData?.groups ?? []}
            activeGroupName={activeGroupName}
            onActiveGroupNameChange={setActiveGroupName}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            qtySoldByProductId={qtySoldByProductId}
            tradeInQtyByProductId={tradeInQtyByProductId}
            onQtySoldChange={(productId, qty) =>
              setQtySoldByProductId((prev) => ({ ...prev, [productId]: qty }))
            }
            onTradeInQtyChange={(productId, qty) =>
              setTradeInQtyByProductId((prev) => ({ ...prev, [productId]: qty }))
            }
            availableQtyByProductId={availableQtyByProductId}
            cachedImageSrcByProductId={cachedImageSrcByProductId}
            brokenImageProductIds={brokenImageProductIds}
            onBrokenImage={(productId) =>
              setBrokenImageProductIds((prev) => ({ ...prev, [productId]: true }))
            }
            canEdit={canEditSelectedDate && !isSubmittedLocked}
            needsDateFirst={showEntryDateSection && !canEditSelectedDate}
            onNeedDateFirst={() => setShowDateRequiredPrompt(true)}
            loadingGroups={loading && productGroupNames.length === 0}
          />

          {canEditSelectedDate ? (
            <div className="mt-5">
              {submitError ? <div className="mt-2 text-sm text-red-600">{submitError}</div> : null}

              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting || isSubmittedLocked}
                className="mt-3 w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Proceed"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {showDateRequiredPrompt ? (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-start justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Select the date of sales</div>
              <div className="text-sm text-slate-600 mt-1">
                Please select the date of sales above before entering quantities.
              </div>
            </div>
            <div className="p-4">
              <button
                type="button"
                className="w-full rounded-xl bg-primary text-white py-2.5 text-sm font-semibold"
                onClick={() => setShowDateRequiredPrompt(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {submittedDatePromptKey ? (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-start justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Sales already submitted</div>
              <div className="text-sm text-slate-600 mt-1">
                Sales has already been submitted for{" "}
                <span className="font-semibold">
                  {malaysiaDateLabelWithWeekdayFromDate(dateKeyToUTCDate(submittedDatePromptKey))}
                </span>
                . Please refer to <span className="font-semibold">Sales History</span> to view the submission details.
              </div>
            </div>
            <div className="p-4 flex flex-col gap-2">
              <button
                type="button"
                className="w-full rounded-xl bg-primary text-white py-2.5 text-sm font-semibold"
                onClick={() => {
                  dismissSubmittedDatePrompt();
                  navigate("/app/sales/history");
                }}
              >
                Go to Sales History
              </button>
              <button
                type="button"
                className="w-full rounded-xl border border-gray-200 bg-white text-slate-700 py-2.5 text-sm font-semibold"
                onClick={dismissSubmittedDatePrompt}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
}

